import type { Character } from "@cloudnest/horror-race-engine";
import { ALL_CHARACTERS } from "@cloudnest/horror-race-engine";
import { Transition } from "@headlessui/react";
import { useState } from "react";
import { CHARACTER_COLORS, CHARACTER_LABELS } from "./character-display";
import { HorrorCharacterIcon } from "./HorrorCharacterIcon";
import { Button } from "./ui/button";

interface SecretSelectionProps {
  readonly onSelect: (characters: readonly [Character, Character]) => void;
  readonly disabled: boolean;
}

export function SecretSelection({ onSelect, disabled }: SecretSelectionProps) {
  const [selected, setSelected] = useState<Character[]>([]);

  const toggleCharacter = (character: Character) => {
    if (disabled) return;
    if (selected.includes(character)) {
      setSelected(selected.filter((c) => c !== character));
    } else if (selected.length < 2) {
      setSelected([...selected, character]);
    }
  };

  const handleConfirm = () => {
    if (selected.length === 2) {
      onSelect([selected[0], selected[1]] as readonly [Character, Character]);
    }
  };

  return (
    <div className="py-6 text-center">
      <h2
        className="mb-2 font-bold text-lg"
        style={{ color: "var(--cn-horror-p1)" }}
      >
        비밀 캐릭터 선택
      </h2>
      <p
        className="mb-6 text-sm"
        style={{ color: "var(--cn-horror-text-muted)" }}
      >
        응원할 몬스터 2마리를 선택하세요
      </p>

      <fieldset
        aria-label="캐릭터 선택"
        className="m-0 mb-6 flex flex-wrap justify-center gap-3 border-none p-0"
      >
        {ALL_CHARACTERS.map((character) => {
          const isSelected = selected.includes(character);
          const color = CHARACTER_COLORS[character];
          return (
            <button
              key={character}
              type="button"
              data-testid={`select-${character}`}
              onClick={() => toggleCharacter(character)}
              disabled={disabled}
              aria-pressed={isSelected}
              className="flex min-w-[100px] cursor-pointer flex-col items-center gap-1 rounded-xl px-5 py-4 text-sm transition-colors focus:outline-none disabled:cursor-not-allowed"
              style={{
                color: "var(--cn-horror-text)",
                backgroundColor: isSelected
                  ? "var(--cn-horror-track)"
                  : "var(--cn-horror-card-bg)",
                border: `2px solid ${
                  isSelected ? color : "var(--cn-horror-surface-border)"
                }`,
                boxShadow: isSelected ? `0 0 12px ${color}` : "none",
              }}
            >
              <HorrorCharacterIcon
                character={character}
                size={36}
                color={color}
              />
              <span>{CHARACTER_LABELS[character]}</span>
            </button>
          );
        })}
      </fieldset>

      <Transition
        show={selected.length === 2}
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Button
          data-testid="confirm-selection-btn"
          onClick={handleConfirm}
          disabled={disabled || selected.length !== 2}
          variant="primary"
          size="lg"
        >
          선택 확정 ({selected.length}/2)
        </Button>
      </Transition>

      {selected.length < 2 && <div className="h-[44px]" aria-hidden="true" />}
    </div>
  );
}
